import { useState, useEffect } from 'react';

import { brandController, attachmentController } from '@/apis/core';
import { BrandAsset, CampaignDetail } from '@/apis/core/types';

interface ExtendedAsset extends BrandAsset {
  file?: File;
  url?: string;
}

const useCampaignAsset = (
  campaign?: CampaignDetail
): { logo?: ExtendedAsset; banner?: ExtendedAsset } => {
  const [brandAssets, setBrandAssets] = useState<BrandAsset[]>([]);
  const [logo, setLogo] = useState<ExtendedAsset>();
  const [banner, setBanner] = useState<ExtendedAsset>();

  const fetchAllBrandAssets = async (brandUid: string) => {
    const assets = await brandController.getBrandAssets(brandUid);
    setBrandAssets(assets);
  };

  const downloadAsset = async (asset: BrandAsset) => {
    const data = await attachmentController.download(asset.attachmentUuid, {
      filename: asset.name,
    });

    const extendedAsset = {
      ...asset,
      ...data,
      url: data?.file ? URL.createObjectURL(data.file) : undefined,
    };

    if (asset.type === 'LOGO' && data) {
      setLogo(extendedAsset);
    }
    if (asset.type === 'BANNER' && data) {
      setBanner(extendedAsset);
    }
  };

  useEffect(() => {
    if (!!campaign?.campaignLogo || !!campaign?.campaignBanner) {
      fetchAllBrandAssets(campaign.brand.uid);
      return () => {
        setBrandAssets([]);
        setLogo(undefined);
        setBanner(undefined);
      };
    }
  }, [campaign]);

  useEffect(() => {
    return () => {
      banner?.url && URL.revokeObjectURL(banner.url);
    };
  }, [banner]);

  useEffect(() => {
    return () => {
      logo?.url && URL.revokeObjectURL(logo.url);
    };
  }, [logo]);

  useEffect(() => {
    if (campaign) {
      const targetLogo = brandAssets.find(
        (asset) =>
          asset.type === 'LOGO' &&
          asset.attachmentUuid === campaign.campaignLogo
      );
      const targetBanner = brandAssets.find(
        (asset) =>
          asset.type === 'BANNER' &&
          asset.attachmentUuid === campaign.campaignBanner
      );

      setLogo(targetLogo);
      setBanner(targetBanner);

      targetLogo && downloadAsset(targetLogo);
      targetBanner && downloadAsset(targetBanner);
    }
  }, [brandAssets, campaign]);

  return {
    logo,
    banner,
  };
};

export default useCampaignAsset;
